import { useState } from "react";
import { useDebouncedSearch } from "@/hooks/use-debounced-search";

export interface MapboxSearchResult {
  mapbox_id: string;
  name: string;
  country_code: string;
  full_address: string | null;
  latitude: number;
  longitude: number;
  bbox_north?: number | null;
  bbox_south?: number | null;
  bbox_east?: number | null;
  bbox_west?: number | null;
}

interface MapboxSearchProps {
  searchPath: string;
  label?: string;
  placeholder?: string;
  onSelect: (result: MapboxSearchResult) => void;
}

export function MapboxSearch({
  searchPath,
  label = "Search for a place",
  placeholder = "e.g. Bangkok, Bali, Syros...",
  onSelect,
}: MapboxSearchProps) {
  const { query, setQuery, setQuerySilently, results, state, reset } = useDebouncedSearch<MapboxSearchResult>(
    searchPath,
    { delayMs: 300 },
  );
  const [dismissed, setDismissed] = useState(false);
  const isOpen = !dismissed && state === "results" && results.length > 0;

  const handleSelect = (result: MapboxSearchResult) => {
    reset();
    setQuerySilently(result.name);
    setDismissed(true);
    onSelect(result);
  };

  return (
    <div className="relative">
      <label htmlFor="mapbox-search" className="mb-1.5 block text-sm font-medium text-secondary">
        {label}
      </label>
      <input
        id="mapbox-search"
        type="text"
        value={query}
        onChange={(e) => {
          setDismissed(false);
          setQuery(e.target.value);
        }}
        onFocus={() => results.length > 0 && setDismissed(false)}
        onBlur={() => setTimeout(() => setDismissed(true), 200)}
        placeholder={placeholder}
        className="w-full rounded-lg border border-primary px-3.5 py-2.5 text-sm text-primary shadow-xs placeholder:text-placeholder focus:border-brand focus:outline-none focus:ring-4 focus:ring-brand/12"
      />

      {isOpen && (
        <ul className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-primary bg-primary shadow-lg">
          {results.map((result) => (
            <li key={result.mapbox_id}>
              <button
                type="button"
                className="w-full px-3 py-2 text-left hover:bg-secondary"
                onMouseDown={() => handleSelect(result)}
              >
                <span className="text-sm font-medium text-primary">{result.name}</span>
                <span className="ml-2 text-sm text-tertiary">{result.full_address ?? result.country_code}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
